import { Loading, SelectField } from '@/components';
import {
  useCommentInputCandidateOrganizations,
  useCommentInputCandidateUsers,
} from '@/features/comment/hooks/input-candidate';
import { useCommentStackSiderView } from '@/features/comment/hooks/stack-sider-view';
import type {
  CommentFormSchema,
  ShareCommentFormSchema,
} from '@/features/comment/models/comment';
import { UserSelectField } from '@/features/tenant-manager/components';
import { useRecursiveOrganizationFilteredByKey } from '@/features/tenant-manager/hooks';
import { NEST_ITEM_CONNECTOR } from '@/utils';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues =
  | z.infer<typeof ShareCommentFormSchema>
  | z.infer<typeof CommentFormSchema>;

export const CommentStrictFields: FC = () => {
  const { t } = useTranslation();

  const stackSiderView = useCommentStackSiderView();
  const commentTarget =
    stackSiderView.target.type === 'project'
      ? {
          key: 'project',
          objectId: stackSiderView.target.projectId,
        }
      : undefined;
  const candidatesUserIds = useCommentInputCandidateUsers({
    params: commentTarget,
    select: (d) => d.userIds,
    enabled: !!commentTarget,
  });

  const { data: candidatesOrganizationIds } =
    useCommentInputCandidateOrganizations({
      params: commentTarget,
      select: (d) => d.organizationIds,
      enabled: !!commentTarget,
    });

  const {
    data: sharedOrganizationOptions,
    isLoading: sharedOrganizationOptionsLoading,
  } = useRecursiveOrganizationFilteredByKey({
    params: {
      ids: candidatesOrganizationIds ?? [],
    },
    enabled: !!candidatesOrganizationIds?.length,
    options: {
      reverse: true,
      filterBy: 'nameWidthId',
    },
    selectOption: {
      referenceFiscalYear:
        stackSiderView.lastItem?.config?.target.projectFiscalYear,
    },
    select: (data) => {
      const seen = new Set<string>();
      return data.flatMap((v) => {
        const label = v.names.join(NEST_ITEM_CONNECTOR);
        const value = v.ids.at(-1);
        if (!value || seen.has(value)) {
          return [];
        }
        seen.add(value);
        return [{ label, value }];
      });
    },
  });

  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();

  if (candidatesUserIds.isLoading || sharedOrganizationOptionsLoading) {
    return <Loading />;
  }

  return (
    <>
      <UserSelectField
        name="assigneeIds"
        label={t('comment.pinpoint-assignee')}
        control={control}
        errors={errors}
        mode="multiple"
        filterIds={candidatesUserIds.data}
        useExistenceCheckOptions={false}
        useNotInOption
        organizationFilter="settingField"
        required
      />
      <SelectField
        name="sharedOrganizationIds"
        label={t('viewable-organization')}
        control={control}
        options={sharedOrganizationOptions}
        errors={errors}
        mode="multiple"
        useNotInOption
      />
      <UserSelectField
        name="relatedUserIds"
        label={t('viewable-user')}
        control={control}
        errors={errors}
        mode="multiple"
        filterIds={candidatesUserIds.data}
        useExistenceCheckOptions={false}
        useNotInOption
        organizationFilter="settingField"
      />
    </>
  );
};
